import React, { useEffect, useState } from "react";
import {
    Box,
    Container,
    Text,
    VStack,
    HStack,
    SimpleGrid,
    Button,
    Heading,
    Icon,
    Accordion,
} from "@chakra-ui/react";
import { DashboardPreview } from "../../Landing/DashboardPreview";
import { useNavigate, useLocation } from "react-router-dom";
import PublicNavbar from "./PublicNavbar";
import PublicFooter from "./PublicFooter";
import { APP_REGISTER_URL } from "../../../services/constants";
import { fetchHomepageHero } from "../../../services/strapi/queries";
import type { HomepageHero } from "../../../services/strapi/types";
import {
    FaUserGraduate,
    FaMoneyBillWave,
    FaChartLine,
    FaCheckCircle,
    FaSchool,
    FaClipboardList,
    FaUserShield,
    FaArrowRight,
} from "react-icons/fa";
import {
    DoodleUnderline,
    DoodleArrow,
    DoodleSparkle,
    WashiTape,
    DoodlePaperclip,
    DoodleCircle,
} from "../../CustomUi/DoodleIcons";

// Hardcoded fallback used until the homepageHero singleton is created in
// Strapi. Keeps the live site rendering correct content during migration.
const FALLBACK_HERO: HomepageHero = {
    eyebrow: "Built for Nigerian schools",
    headline: "Every result. Every fee. In one place.",
    highlightText: "In one place.",
    subheadline:
        "Result handles academic records, report cards, fee collection, and parent communication — all from a single dashboard.",
    primaryCtaLabel: "Get Started — It's Free",
    secondaryCtaLabel: "View Demo",
};

// Render a headline string, highlighting `highlightText` in gold with a hand-drawn doodle underline
function renderHeadline(headline: string, highlight?: string) {
    if (highlight && headline.endsWith(highlight)) {
        const before = headline.slice(0, headline.length - highlight.length);
        return (
            <>
                {before}
                <Box as="span" position="relative" display="inline-block" color="gold.400" whiteSpace="nowrap">
                    {highlight}
                    <DoodleUnderline
                        position="absolute"
                        bottom="-10px"
                        left="0"
                        color="#F59E0B"
                        strokeWidth={4}
                        height="16px"
                    />
                </Box>
            </>
        );
    }
    return headline;
}

import { usePageTitle } from "../../../hooks/usePageTitle";

const LandingPage: React.FC = () => {
    usePageTitle(
        "Result — School Management & Report Card System for Nigerian Schools",
        "Result handles academic records, automated report cards, fee collection, and parent communication for Nigerian schools."
    );
    const navigate = useNavigate();
    const location = useLocation();
    const [hero, setHero] = useState<HomepageHero>(FALLBACK_HERO);

    useEffect(() => {
        let cancelled = false;
        fetchHomepageHero().then((doc) => {
            if (cancelled || !doc) return;
            setHero(doc);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (location.hash) {
            const element = document.getElementById(location.hash.substring(1));
            if (element) {
                setTimeout(() => element.scrollIntoView({ behavior: "smooth" }), 100);
            }
        } else {
            window.scrollTo(0, 0);
        }
    }, [location]);

    const features = [
        { icon: FaUserGraduate, title: "Student Management", description: "Complete student records, enrollment tracking, and academic history — all searchable and exportable." },
        { icon: FaClipboardList, title: "Results & Report Cards", description: "Automated result processing, customisable grade criteria, and bulk PDF report card generation." },
        { icon: FaMoneyBillWave, title: "Result Finance", description: "Dedicated virtual accounts per school. Parents pay in, you track it in real time. No manual reconciliation." },
        { icon: FaUserShield, title: "Parent Portal", description: "Parents log in to see results, download report cards, and view payment history — reducing your admin calls." },
        { icon: FaSchool, title: "Full Audit Trail", description: "Every action logged. Know exactly who did what and when — full staff accountability built in." },
        { icon: FaChartLine, title: "Analytics", description: "Performance trends, financial summaries, and operational insights to help leadership make better decisions." },
    ];

    const faqs = [
        { question: "How does Result Finance work?", answer: "Result Finance gives each school a dedicated virtual account. Parents pay directly into that account, and every transaction is tracked in real time on your dashboard — no manual reconciliation needed." },
        { question: "Is my school's data secure?", answer: "Yes. We use bank-grade encryption, regular automated backups, and comprehensive audit trails. Your data is stored securely and complies with applicable data protection standards." },
        { question: "Can I import existing student data?", answer: "Absolutely. We provide import tools supporting CSV and Excel formats. Our team can also assist with bulk data migration during onboarding." },
        { question: "What support do you offer?", answer: "Email support, documentation, and dedicated account management for larger schools. Nigerian-based support with understanding of local school operations." },
        { question: "How is pricing structured?", answer: "Pricing is based on the number of active students. Schools with fewer than 50 students can use Result completely free — forever." },
    ];

    return (
        <Box bg="white" minH="100vh">
            <PublicNavbar />

            {/* ── Hero ── */}
            <Box bg="brand.900" color="white" pt={[16, 20, 24]} pb={0} overflow="hidden">
                <Container maxW="7xl">
                    <VStack gap={6} textAlign="center" maxW="720px" mx="auto" pb={[12, 14, 16]}>
                        {/* Eyebrow */}
                        <Box
                            px={4} py={1.5}
                            bg="whiteAlpha.100"
                            borderRadius="full"
                            border="1px solid"
                            borderColor="whiteAlpha.150"
                            display="inline-flex"
                        >
                            <Text fontSize="xs" fontWeight="semibold" letterSpacing="widest" textTransform="uppercase" opacity={0.75}>
                                {hero.eyebrow ?? FALLBACK_HERO.eyebrow}
                            </Text>
                        </Box>

                        <Heading
                            as="h1"
                            fontSize={["3xl", "4xl", "5xl", "6xl"]}
                            fontWeight="extrabold"
                            lineHeight="1.1"
                            letterSpacing="-0.02em"
                        >
                            {renderHeadline(hero.headline, hero.highlightText)}
                        </Heading>

                        <Text fontSize={["md", "lg"]} opacity={0.7} maxW="540px" lineHeight="1.75">
                            {hero.subheadline ?? FALLBACK_HERO.subheadline}
                        </Text>

                        <HStack gap={4} flexWrap="wrap" justify="center" pt={2}>
                            <Button
                                size="lg"
                                bg="gold.400"
                                color="#1A1208"
                                px={8}
                                fontWeight="bold"
                                borderRadius="xl"
                                border="2px solid #1A1208"
                                boxShadow="3px 3px 0px #1A1208"
                                transition="all 0.15s ease"
                                _hover={{ bg: "gold.300", transform: "translate(-2px, -2px)", boxShadow: "5px 5px 0px #1A1208" }}
                                onClick={() => (window.location.href = APP_REGISTER_URL)}
                            >
                                {hero.primaryCtaLabel ?? FALLBACK_HERO.primaryCtaLabel}
                            </Button>
                            <Button
                                size="lg"
                                variant="outline"
                                borderColor="whiteAlpha.400"
                                color="white"
                                px={8}
                                fontWeight="semibold"
                                borderRadius="xl"
                                transition="all 0.15s ease"
                                _hover={{ bg: "whiteAlpha.200", transform: "translateY(-2px)" }}
                                onClick={() => navigate("/demo")}
                            >
                                {hero.secondaryCtaLabel ?? FALLBACK_HERO.secondaryCtaLabel}
                            </Button>
                        </HStack>

                        <HStack gap={6} flexWrap="wrap" justify="center" opacity={0.75}>
                            {["No credit card required", "Free for small schools", "Set up in minutes"].map((t) => (
                                <HStack key={t} gap={1.5}>
                                    <Icon as={FaCheckCircle} fontSize="12px" color="gold.400" />
                                    <Text fontSize="sm" fontWeight="medium">{t}</Text>
                                </HStack>
                            ))}
                        </HStack>

                        {/* Whimsical handwritten arrow pointing down */}
                        <VStack gap={1} pt={2} className="font-handwriting" color="gold.300" fontSize="xl" align="center">
                            <HStack gap={1.5}>
                                <Text>Take a peek inside the portal</Text>
                                <DoodleSparkle size={18} color="#FBBF24" />
                            </HStack>
                            <DoodleArrow direction="down-right" color="#FBBF24" width="46px" height="34px" />
                        </VStack>
                    </VStack>

                    {/* Dashboard preview — fully branded, with desk accents */}
                    <DashboardPreview />
                </Container>
            </Box>

            {/* ── Stats with Doodle Accents ── */}
            <Box py={16} bg="#FAF8F2" borderBottom="2px solid" borderColor="brand.900">
                <Container maxW="7xl">
                    <SimpleGrid columns={[2, 4]} gap={8}>
                        {[
                            { stat: "Free", label: "For small schools", sub: "Under 50 students, forever", circled: true },
                            { stat: "₦100", label: "Per transaction", sub: "Flat processor fee, no surprises" },
                            { stat: "One", label: "Dashboard", sub: "Results, fees, parents — all in one place" },
                            { stat: "2020", label: "Founded", sub: "Lagos, Nigeria" },
                        ].map((s) => (
                            <VStack key={s.label} textAlign="center" gap={1}>
                                <Box position="relative" display="inline-block">
                                    <Text fontSize={["2xl", "3xl", "4xl"]} fontWeight="extrabold" color="brand.800" lineHeight="1">{s.stat}</Text>
                                    {s.circled && <DoodleCircle color="#E11D48" strokeWidth={2.4} />}
                                </Box>
                                <Text fontSize="sm" fontWeight="bold" color="gray.800">{s.label}</Text>
                                <Text fontSize="xs" color="gray.500" display={["none", "block"]}>{s.sub}</Text>
                            </VStack>
                        ))}
                    </SimpleGrid>
                </Container>
            </Box>

            {/* ── Features (Playful Notebook Cards) ── */}
            <Box id="features" py={[16, 24]} className="notebook-dots">
                <Container maxW="7xl">
                    <VStack gap={[12, 16]}>
                        <VStack gap={4} textAlign="center" maxW="580px" mx="auto">
                            <HStack gap={2} justify="center">
                                <DoodleSparkle size={20} color="#0A3D2E" />
                                <Text fontSize="xs" fontWeight="extrabold" letterSpacing="widest" textTransform="uppercase" color="brand.800">
                                    Features
                                </Text>
                                <DoodleSparkle size={20} color="#0A3D2E" />
                            </HStack>
                            <Heading as="h2" fontSize={["2xl", "3xl", "4xl"]} fontWeight="extrabold" color="gray.900" lineHeight="1.2">
                                Everything your school needs
                            </Heading>
                            <Text color="gray.600" fontSize="md" lineHeight="1.7">
                                Purpose-built for the way Nigerian and African schools actually operate — simple, reliable, and stress-free.
                            </Text>
                        </VStack>

                        <SimpleGrid columns={[1, 2, 3]} gap={8} w="full">
                            {features.map((f, i) => (
                                <Box
                                    key={i}
                                    p={7}
                                    borderRadius="2xl"
                                    border="2px solid #0A3D2E"
                                    bg="white"
                                    boxShadow="4px 4px 0px #0A3D2E"
                                    position="relative"
                                    style={{
                                        transform: i % 2 === 0 ? "rotate(-0.6deg)" : "rotate(0.6deg)",
                                    }}
                                    transition="all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)"
                                    _hover={{
                                        transform: "translate(-3px, -3px) rotate(0deg) scale(1.02)",
                                        boxShadow: "6px 6px 0px #0A3D2E",
                                    }}
                                >
                                    {i === 1 && (
                                        <WashiTape angle={-4} top="-12px" left="24px" tapeColor="#FEF08A" label="Essential ✏️" />
                                    )}
                                    {i === 2 && (
                                        <DoodlePaperclip color="#10B981" size={32} position="absolute" top="-12px" right="20px" />
                                    )}
                                    <VStack align="start" gap={4} pt={i === 1 ? 2 : 0}>
                                        <Box
                                            p={3}
                                            bg={i % 3 === 0 ? "#DCFCE7" : i % 3 === 1 ? "#FEF9C3" : "#E0F2FE"}
                                            borderRadius="xl"
                                            border="1.5px solid #0A3D2E"
                                            color="brand.900"
                                            display="inline-flex"
                                        >
                                            <Icon as={f.icon} fontSize="xl" />
                                        </Box>
                                        <Text fontWeight="bold" fontSize="lg" color="gray.900">{f.title}</Text>
                                        <Text fontSize="sm" color="gray.600" lineHeight="1.7">{f.description}</Text>
                                    </VStack>
                                </Box>
                            ))}
                        </SimpleGrid>

                        <Button
                            size="lg"
                            bg="white"
                            borderColor="brand.800"
                            border="2px solid #0A3D2E"
                            boxShadow="3px 3px 0px #0A3D2E"
                            color="brand.800"
                            fontWeight="bold"
                            borderRadius="xl"
                            px={8}
                            transition="all 0.15s ease"
                            _hover={{ bg: "brand.50", transform: "translate(-2px, -2px)", boxShadow: "5px 5px 0px #0A3D2E" }}
                            onClick={() => navigate("/features")}
                        >
                            See all features <Icon as={FaArrowRight} fontSize="xs" ml={2} />
                        </Button>
                    </VStack>
                </Container>
            </Box>

            {/* ── How it works ── */}
            <Box py={[16, 24]} bg="gray.50">
                <Container maxW="7xl">
                    <SimpleGrid columns={[1, 2]} gap={[12, 20]} alignItems="center">
                        <VStack align="start" gap={10}>
                            <VStack align="start" gap={4}>
                                <Text fontSize="xs" fontWeight="bold" letterSpacing="widest" textTransform="uppercase" color="brand.800">
                                    Getting started
                                </Text>
                                <Heading as="h2" fontSize={["2xl", "3xl"]} fontWeight="bold" color="gray.900" lineHeight="1.2">
                                    Up and running in three steps
                                </Heading>
                                <Text color="gray.500" fontSize="md" lineHeight="1.7">
                                    No long setup, no IT team required. Most schools are fully live within a day.
                                </Text>
                            </VStack>

                            <VStack align="start" gap={8} w="full">
                                {[
                                    { n: "01", title: "Sign up", body: "Create your school account. No credit card needed." },
                                    { n: "02", title: "Configure", body: "Set up classes, subjects, fee categories, and invite staff." },
                                    { n: "03", title: "Go live", body: "Start entering results and collecting fees — parents get access immediately." },
                                ].map((step) => (
                                    <HStack key={step.n} gap={5} align="start">
                                        <Text fontSize="sm" fontWeight="extrabold" color="brand.800" w="32px" flexShrink={0} pt={0.5}>
                                            {step.n}
                                        </Text>
                                        <Box h="full" w="1px" bg="brand.100" flexShrink={0} display={["none","block"]} />
                                        <VStack align="start" gap={1}>
                                            <Text fontWeight="semibold" color="gray.900">{step.title}</Text>
                                            <Text fontSize="sm" color="gray.500" lineHeight="1.7">{step.body}</Text>
                                        </VStack>
                                    </HStack>
                                ))}
                            </VStack>

                            <Button
                                size="lg"
                                bg="brand.800"
                                color="white"
                                px={8}
                                _hover={{ bg: "brand.900" }}
                                onClick={() => (window.location.href = APP_REGISTER_URL)}
                            >
                                Get started free
                            </Button>
                        </VStack>

                        {/* Right: feature highlight card stack */}
                        <VStack gap={4}>
                            {[
                                { icon: FaClipboardList, title: "Bulk report card generation", body: "Export every student's report card as a print-ready PDF in one click." },
                                { icon: FaMoneyBillWave, title: "Real-time fee tracking", body: "See every payment the moment it hits — no more manual bank reconciliation." },
                                { icon: FaUserShield, title: "Parents stay informed", body: "Parents log in, see results and receipts. Fewer calls to the school office." },
                            ].map((card, i) => (
                                <HStack
                                    key={i}
                                    gap={4}
                                    p={5}
                                    bg="white"
                                    borderRadius="xl"
                                    border="1px solid"
                                    borderColor="gray.100"
                                    w="full"
                                    align="start"
                                    boxShadow="sm"
                                >
                                    <Box p={2.5} bg="brand.50" borderRadius="lg" color="brand.800" flexShrink={0}>
                                        <Icon as={card.icon} fontSize="md" />
                                    </Box>
                                    <VStack align="start" gap={1}>
                                        <Text fontWeight="semibold" fontSize="sm" color="gray.900">{card.title}</Text>
                                        <Text fontSize="xs" color="gray.500" lineHeight="1.6">{card.body}</Text>
                                    </VStack>
                                </HStack>
                            ))}
                        </VStack>
                    </SimpleGrid>
                </Container>
            </Box>

            {/* ── Value statement ── */}
            <Box py={[16, 24]} bg="brand.900" color="white">
                <Container maxW="4xl">
                    <VStack gap={6} textAlign="center">
                        <Text fontSize={["xl", "2xl", "3xl"]} fontWeight="medium" lineHeight="1.6" opacity={0.9}>
                            End-of-term shouldn&apos;t mean three days of printing, collating, and chasing payments.
                            Result handles all of it — so your team can focus on what actually matters.
                        </Text>
                        <Box w={12} h={0.5} bg="gold.400" borderRadius="full" />
                        <Text fontSize="sm" opacity={0.45}>Built for the realities of Nigerian and African schools</Text>
                    </VStack>
                </Container>
            </Box>

            {/* ── Pricing teaser ── */}
            <Box id="pricing" py={[16, 24]} bg="white">
                <Container maxW="7xl">
                    <VStack gap={[10, 14]}>
                        <VStack gap={4} textAlign="center">
                            <Text fontSize="xs" fontWeight="bold" letterSpacing="widest" textTransform="uppercase" color="brand.800">
                                Pricing
                            </Text>
                            <Heading as="h2" fontSize={["2xl", "3xl", "4xl"]} fontWeight="bold" color="gray.900" lineHeight="1.2">
                                Start free. Scale as you grow.
                            </Heading>
                            <Text color="gray.500" maxW="480px" lineHeight="1.7">
                                Schools with fewer than 50 students use Result completely free. A flat ₦100 processor fee applies to all transactions.
                            </Text>
                        </VStack>

                        <SimpleGrid columns={[1, 2, 4]} gap={6} w="full" alignItems="stretch">
                            {[
                                { name: "Free", price: "₦0", note: "Under 50 students", primary: false },
                                { name: "Tier 1", price: "₦5k", note: "50–100 students / mo", primary: false },
                                { name: "Tier 2", price: "₦10k", note: "100–200 students / mo", primary: true },
                                { name: "Tier 3", price: "₦30k", note: "200+ students / mo", primary: false },
                            ].map((plan) => (
                                <Box
                                    key={plan.name}
                                    p={6}
                                    borderRadius="2xl"
                                    border="2px solid #0A3D2E"
                                    bg={plan.primary ? "#FFFBEB" : "white"}
                                    boxShadow={plan.primary ? "5px 5px 0px #0A3D2E" : "3px 3px 0px #0A3D2E"}
                                    textAlign="center"
                                    position="relative"
                                    style={{
                                        transform: plan.primary ? "scale(1.03) rotate(-0.5deg)" : "none",
                                    }}
                                    transition="all 0.2s ease"
                                    _hover={{
                                        transform: "translate(-2px, -2px) scale(1.04)",
                                        boxShadow: "6px 6px 0px #0A3D2E",
                                    }}
                                >
                                    {plan.primary && (
                                        <WashiTape
                                            angle={-2}
                                            top="-14px"
                                            left="50%"
                                            width="120px"
                                            transform="translateX(-50%)"
                                            tapeColor="#FEF08A"
                                            label="POPULAR 🎉"
                                        />
                                    )}
                                    <VStack gap={2} pt={plan.primary ? 2 : 0}>
                                        <Text fontSize="sm" fontWeight="bold" color="gray.600">{plan.name}</Text>
                                        <Text fontSize="3xl" fontWeight="extrabold" color="brand.800">{plan.price}</Text>
                                        <Text fontSize="xs" color="gray.500" fontWeight="medium">{plan.note}</Text>
                                    </VStack>
                                </Box>
                            ))}
                        </SimpleGrid>

                        <Button
                            size="lg"
                            bg="white"
                            border="2px solid #0A3D2E"
                            boxShadow="3px 3px 0px #0A3D2E"
                            color="brand.800"
                            fontWeight="bold"
                            borderRadius="xl"
                            px={8}
                            transition="all 0.15s ease"
                            _hover={{ bg: "brand.50", transform: "translate(-2px, -2px)", boxShadow: "5px 5px 0px #0A3D2E" }}
                            onClick={() => navigate("/pricing")}
                        >
                            See full pricing details <Icon as={FaArrowRight} fontSize="xs" ml={2} />
                        </Button>
                    </VStack>
                </Container>
            </Box>

            {/* ── FAQ ── */}
            <Box id="faq" py={[16, 24]} bg="gray.50">
                <Container maxW="3xl">
                    <VStack gap={10}>
                        <VStack gap={3} textAlign="center">
                            <Text fontSize="xs" fontWeight="bold" letterSpacing="widest" textTransform="uppercase" color="brand.800">
                                FAQ
                            </Text>
                            <Heading as="h2" fontSize={["xl", "2xl", "3xl"]} fontWeight="bold" color="gray.900">
                                Common questions
                            </Heading>
                        </VStack>

                        <Accordion.Root collapsible w="full">
                            {faqs.map((faq, i) => (
                                <Accordion.Item key={i} value={`faq-${i}`} borderBottom="1px solid" borderColor="gray.200">
                                    <Accordion.ItemTrigger py={5}>
                                        <Text fontWeight="semibold" color="gray.900" textAlign="left" fontSize="sm">{faq.question}</Text>
                                    </Accordion.ItemTrigger>
                                    <Accordion.ItemContent pb={5}>
                                        <Text color="gray.600" lineHeight="1.8" fontSize="sm">{faq.answer}</Text>
                                    </Accordion.ItemContent>
                                </Accordion.Item>
                            ))}
                        </Accordion.Root>
                    </VStack>
                </Container>
            </Box>

            {/* ── Final CTA ── */}
            <Box py={[16, 24]} bg="brand.900" color="white">
                <Container maxW="3xl">
                    <VStack gap={6} textAlign="center">
                        <Heading as="h2" fontSize={["2xl", "3xl", "4xl"]} fontWeight="extrabold" lineHeight="1.15">
                            Ready to run a smarter school?
                        </Heading>
                        <Text opacity={0.65} fontSize={["md", "lg"]} maxW="md">
                            Join schools across Nigeria saving time, collecting fees faster, and keeping parents in the loop.
                        </Text>
                        <HStack gap={3} flexWrap="wrap" justify="center" pt={2}>
                            <Button
                                size="lg"
                                bg="gold.500"
                                color="brand.950"
                                px={10}
                                fontWeight="bold"
                                _hover={{ bg: "gold.400" }}
                                onClick={() => (window.location.href = APP_REGISTER_URL)}
                            >
                                Get Started Free
                            </Button>
                            <Button
                                size="lg"
                                variant="outline"
                                borderColor="whiteAlpha.300"
                                color="white"
                                px={8}
                                _hover={{ bg: "whiteAlpha.100" }}
                                onClick={() => navigate("/contact")}
                            >
                                Talk to us
                            </Button>
                        </HStack>
                    </VStack>
                </Container>
            </Box>

            <PublicFooter />
        </Box>
    );
};

export default LandingPage;
